/**
 * The console's colour theme (W4).
 *
 * Dark is the identity and the default — an operator watches this screen for a shift. Light exists for
 * office work and paper, and both are held to WCAG AA by `test/themeContrast.test.ts`, which reads the
 * same `styles/tokens.css` the browser does. Which one is active is the `data-theme` attribute of
 * `<html>`; the tokens do the rest, so no component knows a theme exists.
 *
 * **The choice is NOT persisted.** This console has a structural guard against any browser storage
 * (`test/noBrowserCredentialStore.test.ts`: the session token must die with the tab, and the guard is a
 * blanket needle search on purpose). A remembered theme would need a deliberate exception, which is the
 * owner's call, so until then the choice lives in this tab and in the URL: `?theme=light` opens the
 * console light (a bookmark for a back-office desk), `?theme=dark` forces the NOC palette. Anything
 * else — including a typo — falls back to dark rather than to a half-applied theme.
 */

export const THEMES = ['dark', 'light'] as const;
export type Theme = (typeof THEMES)[number];

export const DEFAULT_THEME: Theme = 'dark';

/** The `<meta name="theme-color">` each theme paints the browser chrome with (the page `surface-base`). */
export const THEME_COLOR: Record<Theme, string> = { dark: '#0c1117', light: '#f3f6f9' };

/** A valid theme from a URL search string, or `null` — never a guess at what a typo meant. */
export function themeFromSearch(search: string): Theme | null {
  const value = new URLSearchParams(search).get('theme');
  return (THEMES as readonly string[]).includes(value ?? '') ? (value as Theme) : null;
}

export function currentTheme(doc: Document = document): Theme {
  const attr = doc.documentElement.getAttribute('data-theme');
  return (THEMES as readonly string[]).includes(attr ?? '') ? (attr as Theme) : DEFAULT_THEME;
}

export function applyTheme(theme: Theme, doc: Document = document): void {
  const root = doc.documentElement;
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme;
  doc.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
}

/** Boot: the URL's choice if it names a theme, else the default. Called before the first render. */
export function initTheme(doc: Document = document, search: string = window.location.search): void {
  applyTheme(themeFromSearch(search) ?? DEFAULT_THEME, doc);
}

export function otherTheme(theme: Theme): Theme {
  return theme === 'dark' ? 'light' : 'dark';
}
